/**
 * The teams slice (state.ext.teams, live over SSE) and small derived views of it. In demo
 * mode (?mock=1) the slice lives in useTeamsDemo and the client changes it in place.
 */
import { useMemo } from 'react';
import { create } from 'zustand';
import { ALL_PROJECTS_WORKSPACE_ID, onDuty } from '@orgus/protocol';
import type { Assignment, MemberPrefs, Person, PresenceViewer, TeamsState, Workspace } from '@orgus/protocol';
import { formatName } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import type { UiError } from '../../lib/api.ts';
import { useMe } from '../auth/useMe.ts';
import { createDemoTeams } from './demo.ts';

export const useTeamsDemo = create<{ state: TeamsState; prefs: Record<string, MemberPrefs> }>()(() => ({
  state: createDemoTeams(Date.now()),
  prefs: {},
}));

/** Change the demo slice (demo mode only). */
export function updateDemo(fn: (s: TeamsState) => TeamsState): void {
  useTeamsDemo.setState((d) => ({ state: { ...fn(d.state), rev: d.state.rev + 1 } }));
}

/**
 * The newest live slice. GET /api/state and the SSE stream can answer out of order; a slice
 * with a lower rev than one already seen is older, so it is ignored (the hub starts rev at
 * its clock, so a restart still moves it forward).
 */
const useTeamsLive = create<{ state: TeamsState | null }>()(() => ({ state: null }));
export function acceptTeamsSlice(next: unknown): void {
  const t = next as TeamsState | undefined;
  if (!t || typeof t.rev !== 'number') return;
  const cur = useTeamsLive.getState().state;
  if (t === cur || (cur && t.rev < cur.rev)) return;
  useTeamsLive.setState({ state: t });
}
useOrgus.subscribe((s) => {
  // A fresh start of the data flow (sign-out, another hub): forget the old slice.
  if (s.phase === 'loading' && !s.ext.teams) {
    if (useTeamsLive.getState().state) useTeamsLive.setState({ state: null });
    return;
  }
  acceptTeamsSlice(s.ext.teams);
});

export type TeamsStatus = 'loading' | 'ready' | 'unsupported' | 'error';

/** The teams slice and whether it is there: 'unsupported' means a hub without the teams module. */
export function useTeams(): { status: TeamsStatus; data: TeamsState | null; error: UiError | null } {
  const mode = useOrgus((s) => s.mode);
  const phase = useOrgus((s) => s.phase);
  const error = useOrgus((s) => s.loadError);
  const live = useTeamsLive((s) => s.state);
  const demo = useTeamsDemo((s) => s.state);
  if (mode === 'mock') return { status: 'ready', data: demo, error: null };
  if (live) return { status: 'ready', data: live, error: null };
  if (phase === 'loading') return { status: 'loading', data: null, error: null };
  if (phase === 'error') return { status: 'error', data: null, error };
  return { status: 'unsupported', data: null, error: null };
}

/** The signed-in person's login, lowercase; 'owner' on a hub without sign-in; null for an agent token or before /api/me answers. */
export function useMyLogin(): string | null {
  const status = useMe((s) => s.status);
  const me = useMe((s) => s.me);
  if (status === 'unsupported') return 'owner';
  if (!me || me.actor.kind !== 'human') return null;
  return me.actor.id.toLowerCase();
}

export function personOf(state: TeamsState | null, login: string): Person {
  const l = login.toLowerCase();
  return state?.people.find((p) => p.login === l) ?? { login: l, name: l, role: 'viewer' };
}

/** "Maya Chen", or "You". */
export function nameOf(state: TeamsState | null, login: string, me: string | null): string {
  return me && login.toLowerCase() === me ? 'You' : formatName(personOf(state, login).name);
}

export function useAssignment(key: string): Assignment | undefined {
  const { data } = useTeams();
  return data?.assignments.find((a) => a.key === key);
}

/** Everyone else looking at a view now (never yourself). */
export function useViewers(view: string | null): PresenceViewer[] {
  const { data } = useTeams();
  const me = useMyLogin();
  return useMemo(() => (view ? (data?.presence[view] ?? []).filter((v) => v.login !== me) : []), [data, view, me]);
}

export function workspaceById(state: TeamsState | null, id: string | null): Workspace | undefined {
  return state?.workspaces.find((w) => w.id === (id ?? ALL_PROJECTS_WORKSPACE_ID));
}

/** Who is on duty for each project now. */
export function dutyMap(state: TeamsState | null, now: number): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const o of state?.ownership ?? []) out[o.projectId] = onDuty(o.rota, now);
  return out;
}

/** Words for why an item is with someone. */
export const REASON_LABEL: Record<Assignment['reason'], string> = {
  mention: 'Assigned',
  routed: 'Routed',
  escalated: 'Escalated',
  claimed: 'Claimed',
};
